import { useState, useEffect, useCallback } from 'react';
import { FileText, Edit2, Save, X, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = ['GBP', 'EUR', 'USD', 'TRY'];

export default function MySales() {
  const { user, getAuthHeader, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editCurrency, setEditCurrency] = useState('GBP');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // If admin, fetch users list
      if (user?.role === 'admin') {
        const usersRes = await axios.get(`${API_URL}/api/users`, getAuthHeader());
        setUsers(usersRes.data);
      }
      
      // Fetch sales records
      let url = `${API_URL}/api/sales-records`;
      if (selectedUser && user?.role === 'admin') {
        url = `${API_URL}/api/sales-records/user/${encodeURIComponent(selectedUser)}`;
      }
      
      const recordsRes = await axios.get(url, getAuthHeader());
      setRecords(recordsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, user, selectedUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditAmount(record.payment_amount?.toString() || '0');
    setEditCurrency(record.currency || 'GBP');
  };

  const handleSave = async () => {
    if (!editingRecord) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/sales-records/${editingRecord.id}`,
        {
          payment_amount: parseFloat(editAmount) || 0,
          currency: editCurrency
        },
        getAuthHeader()
      );
      
      toast.success('Kayıt güncellendi');
      setEditingRecord(null);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value, currency = 'TRY') => {
    const amount = Math.round(value || 0).toLocaleString('tr-TR');
    switch(currency) {
      case 'USD': return `$${amount}`;
      case 'EUR': return `€${amount}`;
      case 'GBP': return `£${amount}`;
      case 'TRY': 
      default: return `₺${amount}`;
    }
  };

  // Staff için iptal kayıtlarını sadece mevcut ayda göster
  const getCurrentMonthKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  
  const getRecordMonthKey = (record) => {
    const dateStr = record.ticket_date || record.treatment_date || '';
    if (!dateStr) return '';
    // Parse date string - handle formats like "2024-01-15" or "15.01.2024"
    const datePart = dateStr.split(' ')[0];
    if (datePart.includes('-') && datePart.length >= 7) {
      return datePart.substring(0, 7); // YYYY-MM
    }
    if (datePart.includes('.')) {
      const parts = datePart.split('.');
      if (parts.length >= 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}`; // YYYY-MM from DD.MM.YYYY
      }
    }
    return '';
  };
  
  // Filter records: Staff can only see cancelled patients from current month
  const filteredRecords = user?.role === 'admin' 
    ? records 
    : records.filter(r => {
        if (!r.is_cancelled) return true; // Non-cancelled records always visible
        // For cancelled records, only show if in current month
        const recordMonth = getRecordMonthKey(r);
        const currentMonth = getCurrentMonthKey();
        return recordMonth === currentMonth;
      });

  // Calculate totals by currency (exclude cancelled)
  const activeRecords = filteredRecords.filter(r => !r.is_cancelled);
  const cancelledCount = filteredRecords.filter(r => r.is_cancelled).length;
  const totals = activeRecords.reduce((acc, r) => {
    const curr = r.currency || 'TEXT';
    if (!acc[curr]) acc[curr] = { count: 0, amount: 0 };
    acc[curr].count += 1;
    acc[curr].amount += r.payment_amount || 0;
    return acc;
  }, {});

  const needsEditCount = records.filter(r => r.needs_edit || r.currency === 'TEXT').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-sales-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope]">Satış Kayıtlarım</h1>
          <p className="text-muted-foreground">Satış kayıtlarınızı görüntüleyin ve düzenleyin</p>
        </div>
        
        {user?.role === 'admin' && (
          <Select value={selectedUser || "all"} onValueChange={(val) => setSelectedUser(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[250px]" data-testid="user-filter">
              <SelectValue placeholder="Tüm Kullanıcılar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.email}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Kayıt</p>
                <p className="text-2xl font-bold">{records.length}</p>
                <p className="text-xs text-muted-foreground">{activeRecords.length} aktif</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        {cancelledCount > 0 && (
          <Card className="border-red-300">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">İptal Edilen</p>
                  <p className="text-2xl font-bold text-red-600">{cancelledCount}</p>
                </div>
                <X className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        )}

        {needsEditCount > 0 && (
          <Card className="border-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Düzenleme Bekliyor</p>
                  <p className="text-2xl font-bold text-amber-600">{needsEditCount}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        )}

        {Object.entries(totals).filter(([k]) => k !== 'TEXT').map(([currency, data]) => (
          <Card key={currency}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{currency} Toplam</p>
                  <p className="text-xl font-bold">{formatCurrency(data.amount, currency)}</p>
                  <p className="text-xs text-muted-foreground">{data.count} kayıt</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope]">Satış Kayıtları</CardTitle>
          <CardDescription>
            {needsEditCount > 0 
              ? `${needsEditCount} kayıt düzenleme bekliyor (sarı ile işaretli)`
              : 'Tüm kayıtlar güncel'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Hasta</TableHead>
                  <TableHead>Ödeme</TableHead>
                  <TableHead>Döviz</TableHead>
                  <TableHead>Tür</TableHead>
                  <TableHead>Ham Veri</TableHead>
                  {isAdmin() && <TableHead className="text-right">İşlem</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.length > 0 ? filteredRecords.slice(0, 200).map((record) => (
                  <TableRow 
                    key={record.id}
                    className={
                      record.is_cancelled 
                        ? 'bg-red-50 dark:bg-red-950/20 line-through opacity-70' 
                        : record.needs_edit || record.currency === 'TEXT' 
                          ? 'bg-amber-50 dark:bg-amber-950/20' 
                          : ''
                    }
                  >
                    <TableCell className="whitespace-nowrap">
                      {record.treatment_date?.split(' ')[0] || record.ticket_date?.split(' ')[0] || '-'}
                    </TableCell>
                    <TableCell className="font-medium max-w-[150px] truncate">
                      {record.patient_name || '-'}
                    </TableCell>
                    <TableCell className="font-bold">
                      {record.currency && record.currency !== 'TEXT' 
                        ? formatCurrency(record.payment_amount, record.currency)
                        : record.payment_amount || 0
                      }
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={record.is_cancelled ? 'destructive' : record.currency === 'TEXT' || record.needs_edit ? 'destructive' : 'secondary'}
                      >
                        {record.currency || 'TEXT'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 items-center">
                        <Badge variant="outline">{record.sales_type || '-'}</Badge>
                        {record.is_cancelled && (
                          <Badge variant="destructive" className="text-xs">İPTAL</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">
                      {record.payment_raw || '-'}
                    </TableCell>
                    {isAdmin() && (
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEdit(record)}
                          disabled={record.is_cancelled}
                          data-testid={`edit-${record.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={isAdmin() ? 7 : 6} className="text-center py-8 text-muted-foreground">
                      Kayıt bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {filteredRecords.length > 200 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              İlk 200 kayıt gösteriliyor (toplam: {filteredRecords.length})
            </p>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog - Admin Only */}
      {isAdmin() && (
        <Dialog open={!!editingRecord} onOpenChange={() => setEditingRecord(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">Kaydı Düzenle</DialogTitle>
              <DialogDescription>
                {editingRecord?.patient_name} - {editingRecord?.payment_raw}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Ödeme Tutarı</label>
                <Input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="0"
                  data-testid="edit-amount-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Para Birimi</label>
                <Select value={editCurrency} onValueChange={setEditCurrency}>
                  <SelectTrigger data-testid="edit-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingRecord(null)}>
                <X className="h-4 w-4 mr-2" />
                İptal
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Kaydet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
